import { client } from "api/client";
import { isUnauthorized } from "api/apolloErrorUtils";
import { LOGOUT } from "api/query";
import { queryClient } from "api/queryClient";
import { readUser } from "api/user";
import {
  explorerQuerySessionKeys,
  resetExplorerState,
} from "pages/project/service/explorer/sessionState";
import { authStore } from "stores/authStore";
import { APP_STATE, commonStore } from "stores/commonStore";
import { notificationStore } from "stores/notificationStore";
import { serviceStore } from "stores/serviceStore";
import { userStore } from "stores/userStore";
import {
  COLOR_MODE_STORAGE_KEY,
  COMMAND_PALETTE_RECENTS_STORAGE_KEY,
  REDIRECT_URL,
  THEME_PREFERENCE_STORAGE_KEY,
} from "utils/localStorage";
import { closeSqlWidgetSessions } from "utils/sqlWidgetSessions";

// Logout clears localStorage except for these device preferences. Entries that
// belong to a user must be keyed by user id (like the palette recents), so the
// next person to sign in on this browser can't see them.
const LOCAL_STORAGE_KEPT_ON_LOGOUT = [
  COLOR_MODE_STORAGE_KEY,
  THEME_PREFERENCE_STORAGE_KEY,
  COMMAND_PALETTE_RECENTS_STORAGE_KEY,
];

/**
 * Clears the data of the user from the browser storage, as a logout does. Keeps
 * the device preferences in `LOCAL_STORAGE_KEPT_ON_LOGOUT`. With
 * `keepRedirectUrl`, it also keeps the page to return to after the login.
 */
export const clearStoredUserData = ({ keepRedirectUrl = false } = {}) => {
  const keptEntries = LOCAL_STORAGE_KEPT_ON_LOGOUT.flatMap((key) => {
    const value = localStorage.getItem(key);
    return value === null ? [] : [[key, value] as const];
  });
  const redirectUrl = keepRedirectUrl
    ? sessionStorage.getItem(REDIRECT_URL)
    : null;

  localStorage.clear();

  keptEntries.forEach(([key, value]) => localStorage.setItem(key, value));

  sessionStorage.clear();

  if (redirectUrl) sessionStorage.setItem(REDIRECT_URL, redirectUrl);
};

type LogoutOptions = {
  /** Stored for post-login restore. */
  redirectUrl?: string;
  /** Path to land on. Defaults to the login page. */
  destination?: string;
  /**
   * Why the app logs out, when the user did not ask for it. With a reason, a
   * failed `LOGOUT` does not stop the logout.
   * - `sessionEnded`: the server answered 401, so the session ended already.
   * - `sessionUnknown`: `GET_USER` failed, so the app does not know if a
   *   session is active. Then the logout also runs without a cached user.
   */
  reason?: "sessionEnded" | "sessionUnknown";
};

// The logout that runs now, and the options of all its calls. No component
// reads them.
let logoutInProgress: Promise<void> | null = null;
let logoutOptions: LogoutOptions = {};

/**
 * Ends the session and clears the local state of the user. The router and the
 * errorLink call it too, so it is a module function, not a hook.
 */
export const logout = (options: LogoutOptions = {}) => {
  // The errorLink calls this for each 401. Run one logout at a time. A second
  // call joins the logout in progress: its options apply, and it ends when
  // that logout ends.
  if (logoutInProgress) {
    // An option that the second call does not give keeps its earlier value.
    const givenOptions = Object.fromEntries(
      Object.entries(options).filter(([, value]) => value !== undefined),
    );
    logoutOptions = { ...logoutOptions, ...givenOptions };
    return logoutInProgress;
  }
  logoutOptions = options;
  logoutInProgress = runLogout().finally(() => {
    logoutInProgress = null;
  });
  return logoutInProgress;
};

// Read `logoutOptions` only when you use them. A second call can change them
// while the logout runs.
const runLogout = async () => {
  try {
    // Without a signed-in user, there is no session to end. The errorLink
    // calls this for each 401, also when no user is signed in.
    if (readUser(client) || logoutOptions.reason === "sessionUnknown") {
      authStore.setProgress(true);

      // Close database sessions while authentication is valid, but wait
      // no more than 500 ms before logout.
      try {
        await Promise.race([
          closeSqlWidgetSessions(explorerQuerySessionKeys()),
          new Promise<void>((resolve) => setTimeout(resolve, 500)),
        ]);
      } catch {
        // Session cleanup errors must not prevent logout.
      }
      try {
        resetExplorerState();
      } catch {
        // Explorer state cleanup errors must not prevent logout.
      }

      // A 401 from `LOGOUT` means that the session ended already. Then
      // clear the local state, as for a successful logout. After another
      // error, the session can still be active: keep it, so that the user
      // can try again. With a `reason`, the app logs out without a user
      // action, so clear the local state after any error.
      try {
        const { errors } = await client.query({
          query: LOGOUT,
          fetchPolicy: "no-cache",
        });
        if (errors?.some((error) => !isUnauthorized(error))) {
          throw errors;
        }
      } catch (error) {
        if (!logoutOptions.reason) {
          throw error;
        }
      }

      // The logout query ends the session on the server. Then clear the
      // local state, and load the page again.
      authStore.resetError();
      clearStoredUserData();
      // The router gates wait for READY. Reset it, so that the next login
      // waits for its own store setup.
      commonStore.setAppState(APP_STATE.INIT);
      userStore.clearStore();
      serviceStore.resetService();
      serviceStore.clearStore();

      await client.clearStore();
      queryClient.clear();

      const { redirectUrl, destination = "/login" } = logoutOptions;
      if (redirectUrl) {
        sessionStorage.setItem(REDIRECT_URL, redirectUrl);
      }

      // Load the page again, not only the route. Then no state of the
      // session that ended stays in memory, and `AuthProvider` decides
      // sign-in from a new `GET_USER`. If `GET_USER` fails after the
      // logout, the app must not show the cached user.
      window.location.assign(destination);
    }
  } catch {
    notificationStore.showErrorToaster("Error logging out, please try again.");
  } finally {
    authStore.setProgress(false);
  }
};
